import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Mail, User, Calendar, Loader2, Shield, Plus, X } from 'lucide-react';
import { format } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface UserDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: {
    id: string;
    name: string;
    email: string;
    type: 'caregiver' | 'specialist';
    created_at: string;
  } | null;
}

type AppRole = 'admin' | 'moderator' | 'user' | 'caregiver' | 'specialist';

export function UserDetailDialog({ open, onOpenChange, user }: UserDetailDialogProps) {
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [userRoles, setUserRoles] = useState<AppRole[]>([]);
  const [selectedRole, setSelectedRole] = useState<AppRole>('user');
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const [assignedSpecialist, setAssignedSpecialist] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);
  const [loadingSpecialist, setLoadingSpecialist] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (user && open) {
      fetchUserRoles();
      if (user.type === 'caregiver') {
        fetchAssignedSpecialist();
      }
    }
  }, [user, open]);

  const fetchAssignedSpecialist = async () => {
    if (!user || user.type !== 'caregiver') return;

    try {
      setLoadingSpecialist(true);
      
      // Get specialist assignment
      const { data, error } = await supabase
        .from('specialist_caregiver')
        .select(`
          specialist:specialists(id, name, email)
        `)
        .eq('caregiver_id', user.id)
        .eq('status', 'active')
        .eq('archived', false)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setAssignedSpecialist(data?.specialist || null);
    } catch (error) {
      console.error('Error fetching assigned specialist:', error);
    } finally {
      setLoadingSpecialist(false);
    }
  };

  const fetchUserRoles = async () => {
    if (!user) return;

    try {
      setLoadingRoles(true);
      
      // Get auth_user_id based on user type
      let userId = null;
      if (user.type === 'caregiver') {
        const { data } = await supabase
          .from('caregivers')
          .select('auth_user_id')
          .eq('id', user.id)
          .single();
        userId = data?.auth_user_id;
      } else {
        const { data } = await supabase
          .from('specialists')
          .select('auth_user_id')
          .eq('id', user.id)
          .single();
        userId = data?.auth_user_id;
      }

      setAuthUserId(userId);

      if (userId) {
        // Use edge function to get roles
        const { data, error } = await supabase.functions.invoke('manage-user-roles', {
          body: { action: 'list', userId }
        });

        if (error) throw error;

        setUserRoles(data.roles || []);
      }
    } catch (error) {
      console.error('Error fetching user roles:', error);
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleAddRole = async () => {
    if (!authUserId || userRoles.includes(selectedRole)) return;

    try {
      const { data, error } = await supabase.functions.invoke('manage-user-roles', {
        body: { 
          action: 'add', 
          userId: authUserId,
          role: selectedRole
        }
      });

      if (error) throw error;

      setUserRoles(data.roles || []);
      toast({
        title: 'Role added',
        description: `${selectedRole} role added and synced to auth metadata`,
      });
    } catch (error: any) {
      console.error('Error adding role:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to add role',
        variant: 'destructive'
      });
    }
  };

  const handleRemoveRole = async (role: AppRole) => {
    if (!authUserId) return;

    try {
      const { data, error } = await supabase.functions.invoke('manage-user-roles', {
        body: { 
          action: 'remove', 
          userId: authUserId,
          role
        }
      });

      if (error) throw error;

      setUserRoles(data.roles || []);
      toast({
        title: 'Role removed',
        description: `${role} role removed and synced to auth metadata`,
      });
    } catch (error: any) {
      console.error('Error removing role:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove role',
        variant: 'destructive'
      });
    }
  };

  if (!user) return null;

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast({
        title: 'Error',
        description: 'Please fill in both subject and message',
        variant: 'destructive'
      });
      return;
    }

    try {
      setSending(true);
      
      const { data, error } = await supabase.functions.invoke('send-user-email', {
        body: {
          to: user.email,
          subject: emailSubject,
          message: emailMessage,
          userName: user.name
        }
      });

      if (error) throw error;

      toast({
        title: 'Email sent',
        description: `Email successfully sent to ${user.name}`,
      });

      // Reset form
      setEmailSubject('');
      setEmailMessage('');
      onOpenChange(false);
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: 'Error',
        description: 'Failed to send email. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            User Profile
          </DialogTitle>
          <DialogDescription>
            View user details and send them an email
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* User Roles Management */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Shield className="h-4 w-4 text-primary" />
              User Roles
            </div>
            
            <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
              {/* Current Roles */}
              <div>
                <Label className="text-xs text-muted-foreground">Current Roles</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {loadingRoles ? (
                    <span className="text-sm text-muted-foreground">Loading...</span>
                  ) : userRoles.length === 0 ? (
                    <span className="text-sm text-muted-foreground">No roles assigned</span>
                  ) : (
                    userRoles.map(role => (
                      <Badge key={role} variant="secondary" className="gap-1">
                        {role}
                        <button
                          onClick={() => handleRemoveRole(role)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))
                  )}
                </div>
              </div>

              {/* Add Role */}
              <div className="flex gap-2">
                <Select value={selectedRole} onValueChange={(value) => setSelectedRole(value as AppRole)}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="caregiver">Caregiver</SelectItem>
                    <SelectItem value="specialist">Specialist</SelectItem>
                    <SelectItem value="moderator">Moderator</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleAddRole}
                  size="sm"
                  disabled={loadingRoles || userRoles.includes(selectedRole)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 border rounded-lg bg-muted/30">
              <div className={`p-3 rounded-lg ${
                user.type === 'caregiver' 
                  ? 'bg-blue-500/10 text-blue-500' 
                  : 'bg-purple-500/10 text-purple-500'
              }`}>
                <User className="h-6 w-6" />
              </div>
              <div className="flex-1 space-y-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <p className="font-semibold text-lg">{user.name}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <p className="text-sm">{user.email}</p>
                </div>
                <div className="flex items-center gap-4 pt-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Type</Label>
                    <p className="text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.type === 'caregiver'
                          ? 'bg-blue-500/10 text-blue-700 dark:text-blue-300'
                          : 'bg-purple-500/10 text-purple-700 dark:text-purple-300'
                      }`}>
                        {user.type.charAt(0).toUpperCase() + user.type.slice(1)}
                      </span>
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Joined
                    </Label>
                    <p className="text-sm">
                      {format(new Date(user.created_at), 'MMM dd, yyyy')} at {format(new Date(user.created_at), 'h:mm a')}
                    </p>
                  </div>
                </div>
                
                {/* Assigned Specialist - Only for caregivers */}
                {user.type === 'caregiver' && (
                  <div className="pt-2 border-t">
                    <Label className="text-xs text-muted-foreground">Assigned Specialist</Label>
                    {loadingSpecialist ? (
                      <div className="flex items-center gap-2 mt-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        <span className="text-sm text-muted-foreground">Loading...</span>
                      </div>
                    ) : assignedSpecialist ? (
                      <div className="mt-1 space-y-1">
                        <p className="text-sm font-medium">{assignedSpecialist.name}</p>
                        <p className="text-xs text-muted-foreground">{assignedSpecialist.email}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground mt-1">No specialist assigned</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Email Form */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Mail className="h-4 w-4 text-primary" />
              Send Email
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                placeholder="Email subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                disabled={sending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Your message to the user..."
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={6}
                disabled={sending}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={sending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendEmail}
                disabled={sending || !emailSubject.trim() || !emailMessage.trim()}
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
